import moment from 'moment';
import { ROUTES, PRIMARY_FLEET_MANAGER, FLEET_MANAGER } from 'constants.js';
import { countriesWhereWalletIsAvailable } from 'config/featureFlags';
import {
  MenuLeftDriversIcon,
  MenuLeftVehiclesIcon,
  MenuLeftSessionsIcon,
  MenuLeftReportsIcon,
  MenuLeftMonthlyStatementsIcon,
  MenuLeftPaymentMethodsIcon,
  MenuLeftWalletIcon,
  MenuLeftUsersIcon,
  MenuLeftAuditLogsIcon,
  MenuLeftBillingIcon,
  MenuLeftSuperAdminHomeIcon,
  MenuLeftStaffAccountsIcon,
  MenuLeftStaffUsersIcon,
  MenuLeftFundingRequestsIcon,
  MenuLeftCreditAccountIcon,
  MenuLeftInvoicesAndStatementsIcon,
} from 'components/Shared/Icons';

export const isWalletNew = walletCreationDate =>
  !walletCreationDate ||
  moment(walletCreationDate)
    .add(1, 'month')
    .isAfter(moment());

// `newFeatureTag` is either a boolean or a function of the current corporation.
export const isNewFeatureTagShown = (menuItem, corporation) =>
  typeof menuItem.newFeatureTag === 'function'
    ? menuItem.newFeatureTag(corporation)
    : Boolean(menuItem.newFeatureTag);

// Attach `feature` key value pair if menu item needs to be feature toggled
export const corporateClientMenuItems = [
  {
    title: 'Drivers',
    key: 'drivers',
    url: ROUTES.DRIVERS,
    component: MenuLeftDriversIcon,
  },
  {
    title: 'Vehicles',
    key: 'vehicles',
    url: ROUTES.VEHICLES,
    subPaths: [ROUTES.VEHICLES_UPLOAD],
    component: MenuLeftVehiclesIcon,
  },
  {
    title: 'Credit account',
    key: 'creditAccount',
    url: ROUTES.CREDIT_ACCOUNT,
    component: MenuLeftCreditAccountIcon,
    feature: 'creditAccount',
    newFeature: true,
  },
  {
    title: 'Sessions',
    key: 'sessions',
    url: ROUTES.SESSIONS,
    component: MenuLeftSessionsIcon,
  },
  {
    title: 'Reports',
    key: 'reports',
    url: ROUTES.REPORTS,
    component: MenuLeftReportsIcon,
  },
  // {
  //   title: 'Monthly Statements',
  //   key: 'monthlyStatements',
  //   url: ROUTES.MONTHLY_STATEMENTS,
  //   feature: 'monthlyStatements',
  //   component: MenuLeftMonthlyStatementsIcon,
  // },
  {
    title: 'Wallet',
    key: 'wallet',
    url: ROUTES.WALLET,
    subPaths: [ROUTES.WALLET_CREATE],
    component: MenuLeftWalletIcon,
    feature: 'wallet',
    newFeatureTag: corporation => isWalletNew(corporation?.walletCreationDate),
  },
  {
    title: 'Payment Info',
    key: 'payments',
    url: ROUTES.PAYMENTS,
    subPaths: [ROUTES.PAYMENTS_CREATE, ROUTES.PAYMENTS_EDIT],
    component: MenuLeftPaymentMethodsIcon,
  },
  {
    title: 'Billing and Subscription',
    key: 'billing',
    url: ROUTES.BILLING,
    component: MenuLeftBillingIcon,
  },
  // {
  //   title: 'Statements',
  //   key: 'invoicesAndStatements',
  //   url: ROUTES.INVOICES,
  //   component: MenuLeftInvoicesAndStatementsIcon,
  //   feature: 'creditAccount',
  // },
  {
    title: 'Invoices and Statements',
    key: 'invoicesAndStatements',
    component: MenuLeftInvoicesAndStatementsIcon,
    children: [
      {
        title: 'Salesforce Invoices',
        key: 'invoices',
        url: ROUTES.SALESFORCE_INVOICES,
        component: MenuLeftInvoicesAndStatementsIcon,
        feature: 'invoices',
        allowedRoles: [PRIMARY_FLEET_MANAGER, FLEET_MANAGER],
      },
      {
        title: 'Statements',
        key: 'statements',
        url: ROUTES.INVOICES,
        component: MenuLeftInvoicesAndStatementsIcon,
        feature: 'creditAccount',
      },
      {
        title: 'Monthly Statements',
        key: 'monthlyStatements',
        url: ROUTES.MONTHLY_STATEMENTS,
        feature: 'monthlyStatements',
        component: MenuLeftMonthlyStatementsIcon,
      },
    ],
  },
  {
    title: 'Users',
    key: 'admins',
    url: ROUTES.USERS,
    component: MenuLeftUsersIcon,
  },
];

export const staffOnlyMenuItems = [
  {
    title: 'Audit Logs',
    key: 'auditLogs',
    url: ROUTES.AUDIT_LOGS,
    component: MenuLeftAuditLogsIcon,
    staffOnly: true,
  },
  {
    title: 'Superadmin home',
    key: 'superAdminHome',
    url: ROUTES.ADMIN_DEFAULT,
    component: MenuLeftSuperAdminHomeIcon,
    staffOnly: true,
  },
];

export const staffUserMenuItems = [
  {
    title: 'Accounts',
    key: 'accounts',
    url: ROUTES.CORPORATIONS_ADMIN,
    component: MenuLeftStaffAccountsIcon,
    superAdminViewOnly: false,
  },
  {
    title: 'Funding Requests',
    key: 'fundingRequests',
    url: ROUTES.FUNDING_REQUESTS,
    component: MenuLeftFundingRequestsIcon,
    superAdminViewOnly: false,
  },
  {
    title: 'PBP Staff Users',
    key: 'staffUsers',
    url: ROUTES.STAFF_USERS,
    component: MenuLeftStaffUsersIcon,
    superAdminViewOnly: true,
  },
];

const deepFindByPath = (items, pathname, parents = []) => {
  for (const item of items) {
    if (item.url === pathname || item.subPaths?.includes(pathname)) {
      return { item: item, parents };
    }
    if (Array.isArray(item.children) && item.children.length) {
      const foundItem = deepFindByPath(item.children, pathname, [...parents, item.key]);
      if (foundItem) return foundItem;
    }
  }
  return null;
};

const getCorporateClientMenuItem = location => {
  const allMenuItems = [...corporateClientMenuItems, ...staffOnlyMenuItems];
  const foundItem = deepFindByPath(allMenuItems, location.pathname);
  return foundItem?.item || undefined;
};

const getStaffUserMenuItem = location => {
  return staffUserMenuItems.find(x => x.url === location.pathname);
};

export const getMenuItemByLocation = (adminRole, location) => {
  if (!location || !location.pathname) {
    return undefined;
  }

  return location.pathname.includes('/admin/') && adminRole
    ? getStaffUserMenuItem(location)
    : getCorporateClientMenuItem(location, adminRole);
};

export const isMenuItemRestricted = (menuItem, creditAccountMigrationConfig, isAdmin, country) => {
  if (
    menuItem.key === 'payments' &&
    creditAccountMigrationConfig?.paymentMethods?.isPageBlocked &&
    !isAdmin
  ) {
    return true;
  }

  if (menuItem.key === 'wallet' && !countriesWhereWalletIsAvailable.includes(country)) {
    return true;
  }

  if (
    menuItem.key === 'creditAccount' &&
    creditAccountMigrationConfig?.creditAccount?.isPageBlocked
  ) {
    return true;
  }

  if (menuItem.title === 'Statements' && creditAccountMigrationConfig?.invoices?.isPageBlocked) {
    return true;
  }

  return false;
};
